// Globale Axios-Konfiguration für Produktion und Entwicklung
// Ziel: Alle direkten axios-Aufrufe (auch mit hartkodierten localhost-URLs)
// auf die korrekte Backend-URL umbiegen und Cookies mitsenden.

import axios from 'axios';
import { getApiBaseUrl } from './utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

const IS_PROD = process.env.NODE_ENV === 'production';

// Standard-Defaults setzen
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.withCredentials = true;

// Einfache CSRF-Token-Verwaltung (nur Production):
// Holt bei Bedarf einmalig ein Token vom Backend und hängt es an mutierende Requests an.
let csrfToken = null;
let csrfPromise = null;

async function ensureCsrfToken() {
  if (csrfToken) return csrfToken;
  if (!csrfPromise) {
    csrfPromise = axios
      .get('/api/csrf-token', { withCredentials: true })
      .then((res) => {
        csrfToken = res?.data?.csrfToken || null;
        return csrfToken;
      })
      .finally(() => {
        // Referenz freigeben, damit ein späterer Fehlversuch erneut fetchen kann
        csrfPromise = null;
      });
  }
  return csrfPromise;
}

// Request-Interceptor: harte localhost-URLs -> API_BASE_URL mappen + CSRF-Header setzen
axios.interceptors.request.use(async (config) => {
  try {
    const originalUrl = config.url || '';
    if (!originalUrl) return config;

    // Wenn absolute localhost-URL verwendet wird, auf API_BASE_URL umschreiben
    if (originalUrl.includes('localhost:5001') || originalUrl.startsWith('http://localhost:5001')) {
      const path = originalUrl.replace(/https?:\/\/localhost:5001/, '');
      config.url = API_BASE_URL + path;
    }

    // Falls jemand versehentlich auf Vercel-Host relativ postet ("/api/..."),
    // lässt baseURL das korrekt auf API_BASE_URL zeigen. Nichts zu tun.

    // In Production: Für mutierende Requests CSRF-Token sicherstellen und Header setzen
    const method = (config.method || 'get').toLowerCase();
    if (IS_PROD && ['post', 'put', 'patch', 'delete'].includes(method)) {
      try {
        const token = await ensureCsrfToken();
        if (token) {
          config.headers = config.headers || {};
          config.headers['x-csrf-token'] = token;
        }
      } catch (e) {
        // Falls Token nicht geholt werden kann, lassen wir den Request laufen;
        // Backend reagiert dann mit 403 und wir sehen das im Log.
      }
    }
  } catch (e) {
    // no-op
  }
  return config;
});

// Optional: Response-Interceptor für einheitliches Logging im Prod-Debug
// (keine Verhaltensänderung, nur Debughilfe)
if (typeof window !== 'undefined') {
  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      const requestUrl = String(err?.config?.url || '');
      const isExpected401 = requestUrl.includes('/api/auth/verify') || requestUrl.includes('/api/auth/session');
      // Konsolenhinweis bei 401, um fehlende Credentials/CORS schnell zu erkennen
      if (err?.response?.status === 401 && !isExpected401) {
        console.warn('⚠️ 401 von Backend – prüfe Session/Credentials/CORS. URL:', err.config?.url);
      }
      return Promise.reject(err);
    }
  );
}

export {}; // stellt sicher, dass die Datei als Modul behandelt wird
